import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import AudioCall from "./AudioCall";
// import VideoCall from "./VideoCall";
// import Button from "../Interactions/content/forms/Elements/Button";
import DiallingScreen from '../Dialler/DiallingScreen';
import { jwtDecode } from "jwt-decode";
import {
  getVoipDevice,
  checkSatelliteCall,
  fetchWalletBalance,
  getVoipToken,
  MIN_WALLET_BAL,
  PSTN_CALL,
} from "../../../services/VoipServices";
import {
  TWILIO_AVOIDABLE_ERROR_CODES, findCallerName,
  notifyBalanceError,
  notifyConnectionError,
} from "../components/Utils";
import { showToast } from "../../../components/ui/toaster";
import { JITSI_DISABLED_DOMAIN_LIST } from "../../../utils/constants";
import { UserServiceClientApi } from "../../../services/api/UserApiServices";
import {
  getLoggedInUserCallHistory,
  clearLoggedInUserCallHistory,
} from "../../../state/actions/user";
import ModalPopup from "../../../components/ui/Modals/ModalMessages/ModalPopup";
import IncomingCallScreen from "../Dialler/IncomingCallScreen";
import CallSummary from "../Dialler/CallSummary";
import { resetCallInfo } from "../../../state/actions/phone";
import VideoCall from "./VideoCall";
import { getAuthData } from "../../../services/storage/LocalStorage";
import PhoneCallModal from "../../../components/ui/Modals/ModalMessages/PhoneCallModal";

const PhoneDevice = () => {
  const dispatch = useDispatch();
  const authData = getAuthData();
  // User state
  const userId = useSelector((state) => state.user.userId) || authData?.user?.userId || null;
  const emailAddress = useSelector((state) => state.user.emailAddress);

  // Contacts
  const contacts = useSelector((state) => state.contacts.accepted);

  // Phone state
  const type = useSelector((state) => state.phone.type);
  const from = useSelector((state) => state.phone.from);
  const to = useSelector((state) => state.phone.to);
  const toName = useSelector((state) => state.phone.toName);
  const reduxToUserId = useSelector((state) => state.phone.toUserId);
  const localCall = useSelector((state) => state.phone.localCall);
  const reduxDiallerState = useSelector((state) => state.phone.diallerState);
  const meetingId = useSelector((state) => state.phone.meetingId);
  const url = useSelector((state) => state.phone.url);
  const fromUserId = useSelector((state) => state.phone.fromUserId);
  const videoEnabled = useSelector((state) => state.phone.videoEnabled);

  // Chats
  const chats = useSelector((state) => state.chats);
  const conversation = useSelector((state) => state.chats.selectedConversation);

  // Domain
  const selectedDomain = useSelector((state) => state.domain.selectedDomainDetails);


  const [diallerState, setDiallerState] = useState("ready");
  const [diallerDir, setDiallerDir] = useState("outgoing");
  const [callDuration, setCallDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isSatellite, setIsSatellite] = useState(false);
  const [videoSessionId, setVideoSessionId] = useState(null);
  const [rejectCall, setRejectCall] = useState(false);
  const [callStartedTime, setCallStartedTime] = useState(0);
  const [toUserIdState, setToUserIdState] = useState(null);
  const [connection, setConnection] = useState(null);
  const [callerId, setCallerId] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [showIncomingCall, setShowIncomingCall] = useState(false);

  const deviceRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let timer;
    if (diallerState === "incoming") {
      timer = setTimeout(() => setShowIncomingCall(true), 3000); // 3 seconds delay
    } else {
      setShowIncomingCall(false); // hide if not incoming
    }
    return () => clearTimeout(timer);
  }, [diallerState]);


  // React to redux diallerState and to changes
  useEffect(() => {
    if (reduxDiallerState) {
      setDiallerState(reduxDiallerState);
      if (reduxDiallerState === "incoming") {
        setCallDuration(0);
        playAudio();
      }
    }
    if (to) setToUserIdState(to);
  }, [reduxDiallerState, to]);

  // Audio helpers
  const playAudio = () => {
    const audio = new Audio("https://s3.amazonaws.com/frontm-contentdelivery-mobilehub-1030065648/media/Hold+Music.mp3");
    audio.loop = true;
    audio.play().catch(console.error);
    audioRef.current = audio;
    setTimeout(stopAudio, 43000);
  };

  const stopAudio = () => {
    if (audioRef.current) audioRef.current.pause();
  };

  const handleCallDisconnect = () => {
    stopAudio();
    if (intervalId) clearInterval(intervalId);
    setDiallerState("call-hung");
    setVideoSessionId(null);
    setIntervalId(null);
    setConnection(null);
    setCallerId(null);
    setIsSatellite(false);
    const now = Date.now();
    dispatch(getLoggedInUserCallHistory(now));
  };

  const handleCallCancel = () => {
    stopAudio();
    if (intervalId) clearInterval(intervalId);
    const now = Date.now();
    dispatch(getLoggedInUserCallHistory(now));
    setDiallerState("call-hung");
    setVideoSessionId(null);
    setConnection(null);
    setIntervalId(null);
  };

  const handleConnectionError = error => {
    console.error("TWILIO ERROR: ", error);
    setDiallerState(null);
    setVideoSessionId(null);
    setConnection(null);
    setCallerId(null);
    if (error.code === 31204) refreshDevice();
    if (!TWILIO_AVOIDABLE_ERROR_CODES.includes(error.code)) {
      notifyConnectionError(error.message);
    }
    stopAudio();
    dispatch(resetCallInfo());
    const now = Date.now();
    dispatch(getLoggedInUserCallHistory(now));
  };

  const handleConnectionEstablished = () => {
    if (!videoEnabled) {
      const id = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
      setIntervalId(id);
    }
    setCallDuration(0);
    setDiallerState("on-call");
    if (type === "sat") playAudio();
  };

  const onClose = () => {
    stopAudio();
    if (intervalId) clearInterval(intervalId);
    setDiallerState(null);
    setDiallerDir(null);
    setConnection(null);
    setCallerId(null);
    dispatch(resetCallInfo());
  };

  const getCallerInfo = () =>
    diallerDir === "incoming"
      ? findCallerName(contacts || [], callerId)
      : toName;

  // Make call
  const makeCall = async () => {
    const caller = from || emailAddress;
    let conn = null;
    if (type === "voip") {
      setIsSatellite(false);
      setDiallerState("on-call");
    } else if (type === "phone") {
      conn = await makePhoneCall(caller);
      setIsSatellite(false);
    } else if (type === "sat") {
      conn = await makeSatCall(caller);
      setIsSatellite(true);
    }
    setConnection(conn);
    setDiallerDir("outgoing");
  };

  const makePhoneCall = async (caller) => {
    const walletBalance = await fetchWalletBalance();
    if (walletBalance < MIN_WALLET_BAL) {
      notifyBalanceError();
      return null;
    }
    return deviceRef.current.connect({
      CallerId: caller,
      To: reduxToUserId
        ? `${to}:${reduxToUserId}${localCall ? `:${localCall}` : ""}`
        : to,
    });
  };

  const makeSatCall = async (caller) => {
    const walletBalance = await fetchWalletBalance();
    if (walletBalance < MIN_WALLET_BAL) {
      notifyBalanceError();
      return null;
    }
    const [callType, msg] = checkSatelliteCall(to);
    if (callType === PSTN_CALL.NOT_SUPPORTED) {
      showToast({ type: "error", message: msg });
      return null;
    }
    if (callType === PSTN_CALL.SAT_CALL) {
      return deviceRef.current.connect({
        CallerId: caller,
        To: reduxToUserId
          ? `SAT:${to.substring(1)}:${reduxToUserId}${localCall ? `:${localCall}` : ""}`
          : `SAT:${to.substring(1)}`,
        FromWeb: true,
      });
    }
    return null;
  };

  const sendDigits = digit => connection?.sendDigits(`${digit}`);

  const toggleMute = () => {
    if (!connection) return;
    if (muted) {
      connection.mute(false);
      setMuted(false);
    } else {
      connection.mute(true);
      setMuted(true);
    }
  };

  const acceptCall = () => {
    if (type === "voip") {
      stopAudio();
      setDiallerState("call-received");
      setCallDuration(0);
      setVideoSessionId(meetingId);
    } else {
      connection?.accept();
    }
  };

  const disconnectCall = () => {
    deviceRef.current?.disconnectAll();
  };

  const rejectCallFunc = async (mode = "receiver", callerMeeting, elapsed, vidEnabled) => {
    stopAudio();
    if (["phone", "sat"].includes(type)) {
      connection?.reject();
    } else {
      setRejectCall(true);
      if (meetingId || callerMeeting) {
        const callAction = "CallEnd";
        let data = {
          callerUserId: userId,
          video: vidEnabled === true,
          callAction,
        };
        if (mode === "initiator") {
          data.userId = toUserIdState;
          data.videoSessionId = callerMeeting;
        } else {
          data.userId = fromUserId;
          data.videoSessionId = meetingId;
        }
        sendVoipCallApi(data, callAction);
        if (mode === "initiator" && !summaryGenerated) {
          sendVoipCallApi(
            {
              userId: toUserIdState,
              callerUserId: userId,
              video: vidEnabled === true,
              videoSessionId: callerMeeting,
              callAction: "CallEnd",
              callDuration: vidEnabled ? elapsed : callDuration,
              callStartTime: callStartedTime,
              callType: "VOIP",
              isLocalContact: false,
            },
            "CallEnd"
          );
          setSummaryGenerated(true);
          setTimeElapsed(elapsed);
        }
      }
      if (type === "voip") {
        await UserServiceClientApi.sendUserAvailableForCall();
      }
    }
    dispatch(clearLoggedInUserCallHistory());
    const now = Date.now();
    dispatch(getLoggedInUserCallHistory(now));
    onClose();
  };

  const sendVideoBotMessage = (action, vidId, callingType, elapsed, vidEnabled) => {
    // dispatch(hideSpinner());
    if (action === "callEnd") {
      rejectCallFunc(callingType, vidId, elapsed, vidEnabled);
    } else if (action === "callActive") {
      stopAudio();
      setCallStartedTime(Date.now());
      handleConnectionEstablished();
    }
  };

  const sendAudioId = (vidId, opts) => {
    if (!vidId) return;
    const data = {
      userId: toUserIdState,
      callerUserId: userId,
      videoSessionId: vidId,
      video: opts?.video || false,
      callAction: "CallStart",
    };
    setVideoSessionId(vidId);
    if (from !== "channel") {
      sendVoipCallApi(data, "CallStart");
    }
    const userDomain = selectedDomain?.userDomain;
    if (!JITSI_DISABLED_DOMAIN_LIST.includes(userDomain)) {
      setDiallerState(null);
      onClose();
    } else {
      setTimeout(() => {
        if (window.localStream) {
          window.localStream.getTracks().forEach(track => track.stop());
        }
        if (callStartedTime === 0 && !summaryGenerated) {
          sendVideoBotMessage("callEnd", vidId, "initiator", timeElapsed, videoEnabled);
        }
      }, 30000);
    }
  };

  const sendVoipCallApi = (dataInfo, actionType) => {
    const userDomain = selectedDomain?.userDomain;
    console.log('dataInfo', dataInfo)
    for (const k in dataInfo) {
      if (dataInfo[k] == null) return;
    }
    console.log("send call api", dataInfo, actionType);
    if (userDomain && actionType === "CallStart") {
      UserServiceClientApi.sendVoipCall({ ...dataInfo, selectedDomain: userDomain })
        .then(res => {
          if (res?.success && actionType === "CallStart") {
            if (JITSI_DISABLED_DOMAIN_LIST.includes(userDomain)) {
              playAudio();
              setCallDuration(0);
              setSummaryGenerated(false);
            }
          } else {
            handleCallDisconnect();
            throw new Error();
          }
        })
        .catch(() => setDiallerState("busy"));
    } else if (userDomain && actionType === "CallEnd") {
      if (dataInfo?.videoSessionId) {
        const decoded = jwtDecode(dataInfo?.videoSessionId);
        if (decoded?.context?.meetingType === 'one2one') {
          UserServiceClientApi.sendVoipCall({ ...dataInfo, selectedDomain: userDomain });
        } else {
          UserServiceClientApi.leaveCall(decoded.context.meetingId, decoded.context.user.email);
        }
      }
    }
    // dispatch(hideSpinner());
  };
  // Render logic:
  if (!diallerState || diallerState === "ready") return null;

  let node = null;
  if (type === "voip") {
    if (diallerState === "on-call" || diallerState === "call-received") {
      node = videoEnabled ? (
        <VideoCall
          sessionInfo={{ userId }}
          audioElement={audioRef.current}
          callingTo={{ toUserid: to, toUserName: toName }}
          callType={type}
          callRejected={rejectCall}
          meetingId={videoSessionId}
          setVideoSessionId={setVideoSessionId}
          videoEnabled={videoEnabled}
          url={url}
          from={from || null}
          disableVideo={false}
          onMeetingIdCreated={sendAudioId}
          incomingVideo={diallerState === "call-received"}
          options={{ video: true }}
          onError={(id, err) => {
            console.error("Meeting error", err);
            sendVideoBotMessage("error", err, id);
          }}
          onMeetingEnded={(id, callingType, elapsed) => {
            sendVideoBotMessage("callEnd", id, callingType, elapsed, true);
          }}
          onMeetingStarted={(id) => {
            sendVideoBotMessage("callActive", id);
          }}
          onJitsiCallJoin={() => {
            setDiallerState(null);
            onClose();
          }}
        />
      ) : (
        <>
          <AudioCall
            sessionInfo={{ userId }}
            callingTo={{ toUserid: to, toUserName: toName }}
            callRejected={rejectCall}
            callDuration={callDuration}
            meetingId={videoSessionId}
            setVideoSessionId={setVideoSessionId}
            from={from || null}
            disableVideo={true}
            url={url}
            onMeetingIdCreated={sendAudioId}
            onError={(id, err) => {
              sendVideoBotMessage("error", err, id);
            }}
            onMeetingEnded={(id, callingType) => {
              sendVideoBotMessage("callEnd", id, callingType, callDuration, false);
              stopAudio();
            }}
            onMeetingStarted={(id) => sendVideoBotMessage("callActive", id)}
            onJitsiCallJoin={() => {
              setDiallerState(null);
              onClose();
            }}
          />
        </>
      );
    }
    if (diallerState === "incoming") {
      node = (
        showIncomingCall && (
          <IncomingCallScreen
            rejectCall={rejectCallFunc}
            from={from}
            acceptCall={acceptCall}
            meetingId={meetingId}
            url={url}
            onClose={rejectCallFunc}
            videoEnabled={videoEnabled}
          />
        )
      );
    }
    if (diallerState === "busy") {
      node = (
        <div className="p-3 d-flex flex-column justify-content-center align-items-center" style={{ textAlign: "center" }}>
          <h4>Ahoy! The contact you're trying to reach is either offline or busy.</h4>
          <p>Please try again later.</p>
          <Button role="button" text="OK" className="btn btn-install ml-3" action={onClose} />
        </div>
      );
    }
  }

  if (diallerState === "call-received" && diallerDir === "incoming" && type !== "voip") {
    node = (
      showIncomingCall && (
        <IncomingCallScreen
          rejectCall={rejectCallFunc}
          from={findCallerName(contacts || [], callerId)}
          meetingId={meetingId}
          url={url}
          acceptCall={acceptCall}
          onClose={rejectCallFunc}
          videoEnabled={videoEnabled}
        />
      )
    );
  }

  if (diallerState === "on-call" && type !== "voip") {
    node = (
      <></>
      // <DiallingScreen
      //   disconnectCall={disconnectCall}
      //   phoneNumber={getCallerInfo()}
      //   onCall={true}
      //   callDuration={callDuration}
      //   onClose={disconnectCall}
      //   sendNumber={sendDigits}
      //   toggleMute={toggleMute}
      //   muted={muted}
      //   diallerDir={diallerDir}
      //   isSatellite={isSatellite}
      // />
    );
  }

  if (diallerState === "call-hung") {
    const userDomain = selectedDomain?.userDomain;
    node = JITSI_DISABLED_DOMAIN_LIST.includes(userDomain) ? (
      <CallSummary
        phoneNumber={getCallerInfo()}
        callDuration={timeElapsed || callDuration}
        onClose={onClose}
      />
    ) : null;
  }

  return (
    <>
      {(node && (diallerState === "incoming" || JITSI_DISABLED_DOMAIN_LIST.includes(selectedDomain?.userDomain))) && (
        <PhoneCallModal size="sm" noHeader className="dialler-modal" keyboard={false} backdrop="static">
          {node}
        </PhoneCallModal>
      )}
      {!JITSI_DISABLED_DOMAIN_LIST.includes(selectedDomain?.userDomain) && diallerState !== "incoming" && node}
      <audio id="self" autoPlay className="local-audio-source" />
    </>
  );
};

export default PhoneDevice;
